/* Board & day — how the console behaves for this salon.
 *
 * Nine settings and a week of opening times. The console explains each of them at
 * length on its own settings page, and that prose is not repeated here: an admin
 * reading this screen knows what a turn is. What IS repeated is the part a
 * salon's own page cannot say — what happens when a key is not set at all, which
 * is a state only this app can produce.
 *
 * Every control writes immediately. There is no Save button on this page because
 * there is no draft: each of these is one row in tt_settings, and a screen that
 * batched them would have to invent a transaction the table does not have.
 */
import * as Vue from 'vue';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { settingKeys } from '../rules/settings-keys.js';
import { ConfirmButton } from '../components/confirm-button.js';

var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                 'Friday', 'Saturday'];

// The console's own three buttons, and its bounds. Restated rather than
// imported for the same reason as the theme presets — see that file's header.
var DUE_CHOICES = [2, 5, 10];
var BUFFER_CHOICES = [0, 5, 10];
// Bounds restated from js/rules/start-offset.js for the same reason.
var OFFSET_CHOICES = [2, 5, 10];
// Dollars; the default restated from js/rules/price-weight.js.
var PRICE_CHOICES = [35, 40, 45];

// What the console takes for bookedClientsGo; the key's own list is in
// admin/js/rules/settings-keys.js.
var GO_CHOICES = [
  { value: 'book', label: 'Follow the book' },
  { value: 'turn', label: 'Turn order' }
];

var Board = {
  name: 'AdminBoard',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var switches = [
      { key: 'requestsOn', label: 'Track client requests',
        hint: 'Off out of the box. The booking system does not say whether a client asked for a technician, so the desk marks it by hand. Turning it off hides requests already recorded rather than erasing them.' },
      { key: 'showNextUp', label: 'Show who is up next',
        hint: 'Adds the Next column to every row of Next availability.' },
      { key: 'autoStop', label: 'Stop a turn when the ticket is closed',
        hint: 'Stops a running turn as of the checkout time. Needs a booking system that reports checkouts.' },
      { key: 'autoStart', label: 'Start the rest of a visit when a client is seated',
        hint: 'Every other service on that appointment that is starting now gets a turn too.' }
    ];

    /* The seven rows the editor shows, padded out of whatever is stored.
     *
     * Deliberately the same shape the console builds in its own store: a day
     * with no entry is closed, and the whole value being null is "nobody has
     * said", which behaves differently — the board then asks nothing of the
     * clock at all and never holds a service back.
     */
    var rows = Vue.computed(function () {
      var raw = store.settingValue('hours');
      var days = (raw && Array.isArray(raw.days)) ? raw.days : [];
      var out = [];
      for (var i = 0; i < 7; i++) {
        var d = days[i];
        out.push({
          dow: i, name: DAY_NAMES[i],
          open: (d && d.open) || '', close: (d && d.close) || '',
          closed: !d
        });
      }
      return out;
    });

    var hoursSet = Vue.computed(function () { return store.isSet('hours'); });

    function writeDays(list) {
      var value = settingKeys.cleanHours({
        days: list.map(function (r) {
          return r.closed ? null : { open: r.open, close: r.close };
        })
      });
      store.setSetting('hours', value);
    }

    // A day being switched on with nothing in its boxes gets the console's own
    // defaults rather than being written as half a row: an open day with no
    // times is a day the board cannot set itself up on, which is the state the
    // switch was meant to leave.
    function setDay(dow, patch) {
      var list = rows.value.map(function (r, i) {
        if (i !== dow) return r;
        var next = Object.assign({}, r, patch);
        if (!next.closed && !next.open) next.open = '09:00';
        if (!next.closed && !next.close) next.close = '19:00';
        return next;
      });
      writeDays(list);
    }

    function copyToAll(dow) {
      var from = rows.value[dow];
      if (!from || from.closed) return;
      writeDays(rows.value.map(function (r) {
        return r.closed ? r : Object.assign({}, r, { open: from.open, close: from.close });
      }));
    }

    function dueValue() { return store.settingValue('dueSoonMinutes'); }
    function bufferValue() { return store.settingValue('conflictBufferMinutes'); }
    function offsetValue() { return store.settingValue('startOffsetMinutes'); }
    function priceValue() { return store.settingValue('fullTurnPrice'); }
    // Unset reads as the book, and so does a stored word the console would not
    // understand — so only an exact 'turn' lights the second button.
    function goesTo() { return store.settingValue('bookedClientsGo') === 'turn' ? 'turn' : 'book'; }

    return {
      switches: switches,
      value: store.settingValue,
      isSet: store.isSet,
      set: store.setSetting,
      clear: store.clearSetting,
      rows: rows,
      hoursSet: hoursSet,
      setDay: setDay,
      copyToAll: copyToAll,
      dueChoices: DUE_CHOICES,
      dueValue: dueValue,
      bufferChoices: BUFFER_CHOICES,
      bufferValue: bufferValue,
      offsetChoices: OFFSET_CHOICES,
      offsetValue: offsetValue,
      priceChoices: PRICE_CHOICES,
      priceValue: priceValue,
      goesTo: goesTo,
      goChoices: GO_CHOICES,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Board &amp; day</h2>',
    '    <p class="page__lead">How the console behaves for this salon. Each row is one',
    '      <code>tt_settings</code> key; <strong>default</strong> means the salon has no row',
    '      for it at all and the board is using the value it ships with.</p>',
    '  </div>',
    '',
    '  <div v-for="s in switches" :key="s.key" class="card">',
    '    <div class="card__head">',
    '      <h3>{{ s.label }}</h3>',
    '      <span class="tag" :class="isSet(s.key) ? \'tag--set\' : \'\'">',
    '        {{ isSet(s.key) ? "set" : "default" }}</span>',
    '    </div>',
    '    <label class="switch">',
    '      <input type="checkbox" :checked="value(s.key)"',
    '        @change="set(s.key, $event.target.checked)" />',
    '      <span class="switch__track"><span class="switch__thumb"></span></span>',
    '      <span class="switch__label">{{ value(s.key) ? "On" : "Off" }}</span>',
    '    </label>',
    '    <p class="hint">{{ s.hint }}</p>',
    '    <div v-if="isSet(s.key)" class="card__row">',
    '      <confirm-button label="Unset" confirm="Delete the row" size="sm" kind="quiet"',
    '        @confirmed="clear(s.key)" />',
    '      <span class="hint hint--inline">Back to the shipped default.</span>',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Due now lead</h3>',
    '      <span class="tag" :class="isSet(\'dueSoonMinutes\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("dueSoonMinutes") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">How many minutes before its start time a booking joins the',
    '      <strong>Due now</strong> row. Ten out of the box. Nothing ever drops off that row',
    '      early &mdash; a client ten minutes late is the most urgent thing on it.</p>',
    '    <div class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="Due now lead">',
    '        <button v-for="m in dueChoices" :key="m" type="button" class="pick__opt"',
    '          :class="{ \'is-on\': dueValue() === m }" role="radio"',
    '          :aria-checked="dueValue() === m ? \'true\' : \'false\'"',
    '          @click="set(\'dueSoonMinutes\', m)">{{ m }} min</button>',
    '      </div>',
    '      <label class="field">',
    '        <span class="field__label">Or a number</span>',
    '        <input class="input input--mins" type="number" min="1" max="120" step="1"',
    '          :value="dueValue()" @change="set(\'dueSoonMinutes\', $event.target.value)" />',
    '      </label>',
    '      <confirm-button v-if="isSet(\'dueSoonMinutes\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clear(\'dueSoonMinutes\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Conflict buffer</h3>',
    '      <span class="tag" :class="isSet(\'conflictBufferMinutes\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("conflictBufferMinutes") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">How far behind a technician can be for a booking before',
    '      <strong>Potential conflicts</strong> lists it. Five out of the box; 0 flags a',
    '      single minute over.</p>',
    '    <div class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="Conflict buffer">',
    '        <button v-for="m in bufferChoices" :key="m" type="button" class="pick__opt"',
    '          :class="{ \'is-on\': bufferValue() === m }" role="radio"',
    '          :aria-checked="bufferValue() === m ? \'true\' : \'false\'"',
    '          @click="set(\'conflictBufferMinutes\', m)">{{ m }} min</button>',
    '      </div>',
    '      <label class="field">',
    '        <span class="field__label">Or a number</span>',
    '        <input class="input input--mins" type="number" min="0" max="60" step="1"',
    '          :value="bufferValue()" @change="set(\'conflictBufferMinutes\', $event.target.value)" />',
    '      </label>',
    '      <confirm-button v-if="isSet(\'conflictBufferMinutes\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clear(\'conflictBufferMinutes\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Early / late start margin</h3>',
    '      <span class="tag" :class="isSet(\'startOffsetMinutes\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("startOffsetMinutes") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">How far a booked turn can start from its booking before the',
    '      progress bar marks it &mdash; green at the start of the bar for early, red for',
    '      late. Five out of the box. Read against the booking as it is now, so moving the',
    '      booking to when the client actually sat down clears the mark.</p>',
    '    <div class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="Early / late start margin">',
    '        <button v-for="m in offsetChoices" :key="m" type="button" class="pick__opt"',
    '          :class="{ \'is-on\': offsetValue() === m }" role="radio"',
    '          :aria-checked="offsetValue() === m ? \'true\' : \'false\'"',
    '          @click="set(\'startOffsetMinutes\', m)">{{ m }} min</button>',
    '      </div>',
    '      <label class="field">',
    '        <span class="field__label">Or a number</span>',
    '        <input class="input input--mins" type="number" min="1" max="60" step="1"',
    '          :value="offsetValue()" @change="set(\'startOffsetMinutes\', $event.target.value)" />',
    '      </label>',
    '      <confirm-button v-if="isSet(\'startOffsetMinutes\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clear(\'startOffsetMinutes\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Full turn price</h3>',
    '      <span class="tag" :class="isSet(\'fullTurnPrice\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("fullTurnPrice") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint">What a sitting has to cost to count as a full turn &mdash; the booked',
    '      service with its options, plus any add-ons folded onto the square. Anything less',
    '      is a half turn, whatever the turn type says. Forty dollars out of the box. A turn',
    '      with no booked price, or one counted by hand on the merge sheet, is left alone.</p>',
    '    <div class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="Full turn price">',
    '        <button v-for="d in priceChoices" :key="d" type="button" class="pick__opt"',
    '          :class="{ \'is-on\': priceValue() === d }" role="radio"',
    '          :aria-checked="priceValue() === d ? \'true\' : \'false\'"',
    '          @click="set(\'fullTurnPrice\', d)">${{ d }}</button>',
    '      </div>',
    '      <label class="field">',
    '        <span class="field__label">Or a number</span>',
    '        <input class="input input--mins" type="number" min="1" max="1000" step="1"',
    '          :value="priceValue()" @change="set(\'fullTurnPrice\', $event.target.value)" />',
    '      </label>',
    '      <confirm-button v-if="isSet(\'fullTurnPrice\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clear(\'fullTurnPrice\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Who takes a booked client</h3>',
    '      <span class="tag" :class="isSet(\'bookedClientsGo\') ? \'tag--set\' : \'\'">',
    '        {{ isSet("bookedClientsGo") ? "set" : "default" }}</span>',
    '    </div>',
    '    <p class="hint"><strong>Follow the book</strong> gives a client to the technician they',
    '      booked, which is how the board has always worked. <strong>Turn order</strong> gives',
    '      them to whoever is next in the rotation. Follow the book out of the box.</p>',
    '    <div class="card__row">',
    '      <div class="pick" role="radiogroup" aria-label="Who takes a booked client">',
    '        <button v-for="c in goChoices" :key="c.value" type="button" class="pick__opt"',
    '          :class="{ \'is-on\': goesTo() === c.value }" role="radio"',
    '          :aria-checked="goesTo() === c.value ? \'true\' : \'false\'"',
    '          @click="set(\'bookedClientsGo\', c.value)">{{ c.label }}</button>',
    '      </div>',
    '      <confirm-button v-if="isSet(\'bookedClientsGo\')" label="Unset" confirm="Delete the row"',
    '        size="sm" kind="quiet" @confirmed="clear(\'bookedClientsGo\')" />',
    '    </div>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__head">',
    '      <h3>Business hours</h3>',
    '      <span class="tag" :class="hoursSet ? \'tag--set\' : \'\'">',
    '        {{ hoursSet ? "set" : "not set" }}</span>',
    '    </div>',
    '    <p class="hint">When the doors open and shut. Two things read them: the board sets',
    '      itself up <strong>45 minutes before opening</strong> rather than at midnight, and',
    '      Next availability stops offering work the day is not long enough for.</p>',
    '    <p class="hint">With nothing set at all, neither happens &mdash; which is a different',
    '      state from seven closed days, and the reason unsetting this key is not the same as',
    '      switching every day off.</p>',
    '    <ul class="hours">',
    '      <li v-for="row in rows" :key="row.dow" class="hours__row" :class="{ \'is-closed\': row.closed }">',
    '        <label class="switch hours__day">',
    '          <input type="checkbox" :checked="!row.closed"',
    '            @change="setDay(row.dow, { closed: !$event.target.checked })" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">{{ row.name }}</span>',
    '        </label>',
    '        <template v-if="!row.closed">',
    '          <input class="input input--clock" type="time" :value="row.open"',
    '            :aria-label="row.name + \' opening time\'"',
    '            @change="setDay(row.dow, { open: $event.target.value })" />',
    '          <span class="hours__dash">&ndash;</span>',
    '          <input class="input input--clock" type="time" :value="row.close"',
    '            :aria-label="row.name + \' closing time\'"',
    '            @change="setDay(row.dow, { close: $event.target.value })" />',
    '          <button type="button" class="link-btn" @click="copyToAll(row.dow)">Copy to all</button>',
    '        </template>',
    '        <span v-else class="hours__shut">Closed</span>',
    '      </li>',
    '    </ul>',
    '    <div class="card__row">',
    '      <confirm-button v-if="hoursSet" label="Clear business hours"',
    '        confirm="Delete the row — the board stops using the clock"',
    '        size="sm" @confirmed="clear(\'hours\')" />',
    '      <button type="button" class="link-btn" @click="goPage(\'advanced\')">',
    '        Edit the raw value</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'board',
  name: 'Board & day',
  group: 'Console settings',
  page: Board
});

export { Board };
